"use strict";
import { nanoid } from "@reduxjs/toolkit";
import { popupRegistry } from "components/Popups/registry";
import { PopupType } from "components/Popups/types";
import { useRef } from "react";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { useIsAtomicBatchingSupportedByChainIdCallback } from "state/walletCapabilities/hooks/useIsAtomicBatchingSupportedByChain";
import { useWalletGetCapabilitiesMutation } from "state/walletCapabilities/hooks/useWalletGetCapabilitiesMutation";
import { isAtomicBatchingSupportedByChainId } from "state/walletCapabilities/lib/handleGetCapabilities";
import { useDelegationService } from "state/wallets/useDelegationService";
import { selectHasShownMismatchToast } from "uniswap/src/features/behaviorHistory/selectors";
import { setHasShownMismatchToast } from "uniswap/src/features/behaviorHistory/slice";
import { FeatureFlags } from "uniswap/src/features/gating/flags";
import { useFeatureFlag } from "uniswap/src/features/gating/hooks";
import { createHasMismatchUtil } from "uniswap/src/features/smartWallet/mismatch/mismatch";
import { getLogger } from "utilities/src/logger/logger";
import { useEvent } from "utilities/src/react/hooks";
export function useHasMismatchCallback() {
  const { mutateAsync } = useWalletGetCapabilitiesMutation();
  const getIsAtomicBatchingSupportedByChainId = useIsAtomicBatchingSupportedByChainIdCallback();
  const isWalletGetCapabilitiesDisabled = useIsWalletGetCapabilitiesDisabled();
  const delegationService = useDelegationService();
  const getIsAtomicBatchingSupported = async (input) => {
    if (isWalletGetCapabilitiesDisabled) {
      return false;
    }
    const isAtomicBatchingSupported = getIsAtomicBatchingSupportedByChainId(input.chainId);
    if (isAtomicBatchingSupported !== void 0) {
      return isAtomicBatchingSupported;
    }
    try {
      const result = await mutateAsync();
      if (result) {
        return isAtomicBatchingSupportedByChainId(result, input.chainId);
      }
      return false;
    } catch (error) {
      getLogger().error(error, {
        tags: {
          file: "useMismatchAccount.ts",
          function: "getIsAtomicBatchingSupported"
        }
      });
      return false;
    }
  };
  const onMismatchDetected = useEvent(
    (payload) => {
    }
  );
  return useEvent(
    createHasMismatchUtil({
      logger: getLogger(),
      delegationService,
      getIsAtomicBatchingSupported,
      onMismatchDetected
    })
  );
}
export const useShowMismatchToast = () => {
  const isPermitMismatchUxEnabled = useFeatureFlag(FeatureFlags.EnablePermitMismatchUX);
  const toastId = useRef(nanoid());
  const showMismatchToast2 = useShowMismatchToastCallback();
  return useEvent(() => {
    if (isPermitMismatchUxEnabled) {
      showMismatchToast2({ id: toastId.current });
    }
  });
};
function useShowMismatchToastCallback() {
  const hasShownMismatchToast = useAppSelector(selectHasShownMismatchToast);
  const dispatch = useAppDispatch();
  return useEvent((input) => {
    if (!hasShownMismatchToast) {
      showMismatchToast(input);
      dispatch(setHasShownMismatchToast(true));
    }
  });
}
function showMismatchToast(input) {
  const key = `mismatch-${input.id}`;
  popupRegistry.addPopup(
    {
      type: PopupType.Mismatch
    },
    key,
    Infinity
  );
}
function useIsWalletGetCapabilitiesDisabled() {
  return useFeatureFlag(FeatureFlags.ForceDisableWalletGetCapabilities);
}
